import { useState, useMemo, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, PackageSearch } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useStoreLookups, useSubmitStoreIndent, useCheckDraftStoreIndentStock } from "@/features/store-indents/api";
import { LocationHierarchyPicker } from "@/features/store-indents/LocationHierarchyPicker";
import type { StoreIndentKind } from "@/features/store-indents/types";
import { useStoresByBranch } from "@/features/masters/api";

export const Route = createFileRoute("/_authenticated/store-indents/new")({
  component: NewStoreIndent,
  validateSearch: (s: Record<string, unknown>) => ({ kind: (s.kind as string) || "spare" }),
});

type Line = {
  spare_id?: string;
  asset_id?: string;
  location_id?: string;
  item_name?: string;
  qty_requested: number;
  remarks?: string;
};

function NewStoreIndent() {
  const navigate = useNavigate();
  const { kind: initialKind } = Route.useSearch();
  const { data: lookups } = useStoreLookups();
  const submit = useSubmitStoreIndent();
  const [kind, setKind] = useState<StoreIndentKind>(
    (["spare", "consumable", "asset"] as const).includes(initialKind as StoreIndentKind) ? (initialKind as StoreIndentKind) : "spare",
  );
  const [branchId, setBranchId] = useState("");
  const [deptId, setDeptId] = useState("");
  const [sourceStoreId, setSourceStoreId] = useState("");
  const [remarks, setRemarks] = useState("");
  const [lines, setLines] = useState<Line[]>([{ qty_requested: 1 }]);
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [stockResults, setStockResults] = useState<any[]>([]);

  const checkDraftStock = useCheckDraftStoreIndentStock();

  const { data: storesForBranch = [] } = useStoresByBranch(branchId || null);

  const departments = useMemo(() => (lookups?.departments ?? []).filter((d) => !branchId || d.branch_id === branchId), [lookups, branchId]);
  const locations = useMemo(() => (lookups?.locations ?? []).filter((l) => !branchId || l.branch_id === branchId), [lookups, branchId]);
  const filteredSpares = useMemo(
    () => (lookups?.spares ?? []).filter((s) => (kind === "consumable" ? s.item_type === "consumable" : s.item_type !== "consumable")),
    [lookups?.spares, kind],
  );
  const assetsInStore = useMemo(() => (lookups?.assetsInStore ?? []).filter((a) => !branchId || a.branch_id === branchId), [lookups, branchId]);

  // Automatically update empty locations when department is selected
  useEffect(() => {
    if (!deptId) return;
    const deptLocation = locations.find((l) => l.kind === "department" && l.department_id === deptId);
    if (deptLocation) {
      setLines((prev) => prev.map((l) => (!l.location_id ? { ...l, location_id: deptLocation.id } : l)));
    }
  }, [deptId, locations]);

  const addLine = () => {
    const deptLocation = deptId ? locations.find((l) => l.kind === "department" && l.department_id === deptId) : undefined;
    setLines((s) => [...s, { qty_requested: 1, location_id: deptLocation?.id }]);
  };
  const removeLine = (idx: number) => setLines((s) => s.filter((_, i) => i !== idx));
  const updateLine = (idx: number, patch: Partial<Line>) =>
    setLines((s) => s.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const onSubmit = async () => {
    if (!branchId) return toast.error("Branch required");
    if (!deptId) return toast.error("Department required");
    if (lines.length === 0) return toast.error("Add at least one item");
    for (const l of lines) {
      if (kind === "asset") {
        if (!l.asset_id) return toast.error("Pick an asset from the store");
        if (!l.location_id) return toast.error("Target location required for asset allocation");
      } else {
        if (!l.spare_id && !(l.item_name?.trim())) return toast.error("Each line needs an item");
        if (!(l.qty_requested > 0) || !Number.isInteger(Number(l.qty_requested))) return toast.error("Quantity must be a positive whole number");
      }
    }
    try {
      const res = await submit.mutateAsync({
        branch_id: branchId,
        department_id: deptId,
        source_store_id: sourceStoreId || undefined,
        kind,
        remarks,
        items: lines.map((l) => (kind === "asset" ? { ...l, qty_requested: 1 } : l)),
      });
      toast.success(`Store indent ${res.store_indent_no} submitted`);
      navigate({ to: "/store-indents/$id", params: { id: res.id } });
    } catch (e) { toast.error((e as Error).message); }
  };

  const handleCheckStock = async () => {
    if (!branchId) return toast.error("Branch required to check stock");
    if (lines.length === 0) return toast.error("Add at least one item");
    
    // Validate items
    for (const l of lines) {
      if (!l.spare_id && !(l.item_name?.trim())) return toast.error("Each line needs an item");
    }

    try {
      const results = await checkDraftStock.mutateAsync({
        branch_id: branchId,
        source_store_id: sourceStoreId || undefined,
        items: lines,
      });
      setStockResults(results);
      setStockDialogOpen(true);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">New Store Indent</h1>

      <Tabs value={kind} onValueChange={(v) => { setKind(v as StoreIndentKind); setLines([{ qty_requested: 1 }]); }}>
        <TabsList>
          <TabsTrigger value="spare">Spares</TabsTrigger>
          <TabsTrigger value="consumable">Consumables</TabsTrigger>
          <TabsTrigger value="asset">Asset Allocation</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardHeader><CardTitle>Header</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Branch *</Label>
            <Select value={branchId} onValueChange={(v) => { setBranchId(v); setDeptId(""); }}>
              <SelectTrigger><SelectValue placeholder="Choose" /></SelectTrigger>
              <SelectContent>
                {(lookups?.branches ?? []).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Department *</Label>
            <Select value={deptId} onValueChange={setDeptId} disabled={!branchId}>
              <SelectTrigger><SelectValue placeholder="Choose" /></SelectTrigger>
              <SelectContent>
                {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Source Store</Label>
            <Select
              value={sourceStoreId || "__all__"}
              onValueChange={(v) => setSourceStoreId(v === "__all__" ? "" : v)}
              disabled={!branchId}
            >
              <SelectTrigger>
                <SelectValue placeholder={branchId ? "Any store (optional)" : "Select branch first"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">— Any store —</SelectItem>
                {storesForBranch.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name} <span className="text-xs text-muted-foreground ml-1">[{s.code}]</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Remarks</Label>
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Items</CardTitle>
          <div className="flex gap-2">
            {kind !== "asset" && (
              <Button size="sm" variant="secondary" onClick={handleCheckStock} disabled={checkDraftStock.isPending || !branchId}>
                {checkDraftStock.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <PackageSearch className="h-4 w-4 mr-1" />}
                Check Stock Availability
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={addLine}><Plus className="h-4 w-4 mr-1" />Add line</Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              {kind === "asset" ? (
                <>
                  <TableHead>Asset in Store</TableHead>
                  <TableHead>Target Location</TableHead>
                  <TableHead>Remarks</TableHead>
                </>
              ) : (
                <>
                  <TableHead>{kind === "consumable" ? "Consumable" : "Spare"}</TableHead>
                  <TableHead>Or Item Name</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Remarks</TableHead>
                </>
              )}
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {lines.map((l, i) => (
                <TableRow key={i}>
                  {kind === "asset" ? (
                    <>
                      <TableCell>
                        <Select value={l.asset_id ?? ""} onValueChange={(v) => updateLine(i, { asset_id: v })}>
                          <SelectTrigger className="min-w-[260px]"><SelectValue placeholder="Choose asset" /></SelectTrigger>
                          <SelectContent className="max-h-72">
                            {assetsInStore.length === 0 && <div className="p-2 text-xs text-muted-foreground">No unallocated assets in this branch.</div>}
                            {assetsInStore.map((a) => (
                              <SelectItem key={a.id} value={a.id}>{a.asset_code} · {a.name}{a.serial_number ? ` (SN ${a.serial_number})` : ""}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <LocationHierarchyPicker
                          locations={locations}
                          branchId={branchId}
                          departmentId={deptId}
                          value={l.location_id}
                          onChange={(id) => updateLine(i, { location_id: id })}
                          hideDepartment={true}
                        />
                      </TableCell>
                      <TableCell><Input value={l.remarks ?? ""} onChange={(e) => updateLine(i, { remarks: e.target.value })} /></TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>
                        <Select value={l.spare_id ?? "__none"} onValueChange={(v) => updateLine(i, { spare_id: v === "__none" ? undefined : v })}>
                          <SelectTrigger className="min-w-[220px]"><SelectValue placeholder={`Choose ${kind}`} /></SelectTrigger>
                          <SelectContent className="max-h-72">
                            <SelectItem value="__none">— None —</SelectItem>
                            {filteredSpares.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell><Input value={l.item_name ?? ""} onChange={(e) => updateLine(i, { item_name: e.target.value })} placeholder="Free text" /></TableCell>
                      <TableCell>
                        <Input 
                          type="number" 
                          step="1" 
                          min="1"
                          value={l.qty_requested} 
                          onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "e" || e.key === "." || e.key === ",") e.preventDefault();
                          }}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "") {
                              updateLine(i, { qty_requested: "" as any });
                              return;
                            }
                            const parsed = parseInt(val, 10);
                            if (Number.isNaN(parsed)) return;
                            updateLine(i, { qty_requested: Math.max(0, parsed) });
                          }} 
                          className="w-24" 
                        />
                      </TableCell>
                      <TableCell>
                        <LocationHierarchyPicker
                          locations={locations}
                          branchId={branchId}
                          departmentId={deptId}
                          value={l.location_id}
                          onChange={(id) => updateLine(i, { location_id: id })}
                          compact
                          hideDepartment={true}
                        />
                      </TableCell>
                      <TableCell><Input value={l.remarks ?? ""} onChange={(e) => updateLine(i, { remarks: e.target.value })} /></TableCell>
                    </>
                  )}
                  <TableCell><Button size="sm" variant="ghost" onClick={() => removeLine(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate({ to: "/store-indents" })}>Cancel</Button>
        <Button onClick={onSubmit} disabled={submit.isPending}>Submit Store Indent</Button>
      </div>

      <Dialog open={stockDialogOpen} onOpenChange={setStockDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader><DialogTitle>Stock Availability</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            {stockResults.map((res, i) => (
              <div key={i} className="p-3 border rounded-md">
                <div className="font-semibold mb-2">{res.spare_name} <span className="text-muted-foreground font-normal ml-2">Req: {res.requested_qty}</span></div>
                {res.spare_id ? (
                  <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                    <div>Total On Hand: <b>{res.total_on_hand}</b></div>
                    <div>Total Reserved: <b>{res.total_reserved}</b></div>
                    <div className="text-primary">Available: <b>{res.available}</b></div>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground mb-3">Free text item — no stock to check</div>
                )}
                {res.stores && res.stores.length > 0 && (
                  <div className="text-sm">
                    <div className="font-medium bg-muted px-2 py-1 mb-1 rounded">Per Store</div>
                    {res.stores.map((s: any, idx: number) => (
                      <div key={idx} className="flex justify-between px-2 py-1 border-b last:border-0">
                        <span>{s.store_location}</span>
                        <span>{s.available}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
